import type { LevelStory } from "@/domain/story/model/level-story";
import {
  type BackgroundName,
  backgroundAssetPath,
  type CompanionExpression,
  companionAssetPath,
} from "./paths";

/**
 * 画像を`decode()`まで完了させる（design.md「先読み戦略」）。`onload`だけだと
 * デコードが描画時に走り、遷移フレームを落とす原因になるため。
 * 失敗（404等）しても先読み全体を失敗させない（プレースホルダ差し替え漏れ等で
 * 演出そのものが止まらないようにする。取得自体は各ビューが表示時に改めて行う）。
 */
function decodeImage(src: string): Promise<void> {
  try {
    const img = new Image();
    img.src = src;
    return img.decode().then(
      () => undefined,
      () => undefined,
    );
  } catch {
    return Promise.resolve();
  }
}

/**
 * 単一の背景画像を`decode()`まで先読みする（稽古画面の情景。D-115）。
 * 話の先読みと同じく、**遷移をこの完了に依存させない**（呼び出し側はawaitせず発火だけする）。
 */
export function preloadBackground(name: BackgroundName): Promise<void> {
  return decodeImage(backgroundAssetPath(name));
}

/**
 * 1レベル分の話（導入・結果ADV）が参照する背景・相棒立ち絵をすべて`decode()`まで先読みする。
 * `levelId`の値は`backgroundPath`/`expression`同様、インフラ層のZod検証（`schema.ts`）を
 * 通過済みである前提でキャストする。
 *
 * **遷移をこの完了に依存させない**（design.md）。呼び出し側は`await`せず発火だけする。
 */
export function preloadLevelStoryAssets(story: LevelStory): Promise<void> {
  const backgroundNames = new Set<string>();
  const expressions = new Set<string>();

  for (const chapter of [story.intro, story.outro]) {
    for (const scene of chapter.scenes) {
      backgroundNames.add(scene.backgroundPath);
      for (const line of scene.lines) {
        expressions.add(line.expression);
      }
    }
  }

  const tasks: Promise<void>[] = [
    ...[...backgroundNames].map((name) => decodeImage(backgroundAssetPath(name as BackgroundName))),
    ...[...expressions].map((expression) =>
      decodeImage(companionAssetPath(expression as CompanionExpression)),
    ),
  ];

  return Promise.all(tasks).then(() => undefined);
}
